import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { api, PAYMENT_METHODS } from '../api.js';
import { useAuth } from '../auth.jsx';

function formatMoney(amount, currency) {
  if (amount == null) return '—';
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: currency || 'CAD' }).format(Number(amount));
}

function ChargebackEditor({ po, concessions, onSaved }) {
  const { t } = useTranslation();
  const [paidById, setPaidById] = useState(po.chargeback?.paidByConcession?.id || '');
  const [method, setMethod] = useState(po.chargeback?.paymentMethod || 'IT_CARD');
  const [splits, setSplits] = useState(
    po.chargeback?.splits?.map((s) => ({
      concessionId: s.concession.id,
      amount: s.amount ?? '',
      percentage: s.percentage ?? '',
      rebilledAt: s.rebilledAt ? s.rebilledAt.slice(0, 10) : '',
      rebilledNote: s.rebilledNote || '',
    })) || []
  );
  const [busy, setBusy] = useState(false);

  const addSplit = () => setSplits((s) => [...s, { concessionId: '', amount: '', percentage: '', rebilledAt: '', rebilledNote: '' }]);
  const updateSplit = (i, k, v) => setSplits((s) => s.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const removeSplit = (i) => setSplits((s) => s.filter((_, j) => j !== i));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/api/purchase-orders/${po.id}/chargeback`, {
        paidByConcessionId: paidById || null,
        paymentMethod: method,
        splits: splits
          .filter((s) => s.concessionId)
          .map((s) => ({
            concessionId: s.concessionId,
            amount: s.amount === '' ? null : Number(s.amount),
            percentage: s.percentage === '' ? null : Number(s.percentage),
            rebilledAt: s.rebilledAt ? new Date(s.rebilledAt).toISOString() : null,
            rebilledNote: s.rebilledNote || null,
          })),
      });
      toast.success(t('po.chargeback.saved'));
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirm(t('po.chargeback.confirmRemove'))) return;
    setBusy(true);
    try {
      await api.del(`/api/purchase-orders/${po.id}/chargeback`);
      toast.success(t('common.deleted'));
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">{t('po.chargeback.title')}</h2>
        {po.chargeback && (
          <button onClick={remove} className="text-xs text-rose-400 hover:text-rose-300">{t('po.chargeback.remove')}</button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">{t('po.chargeback.paidBy')}</label>
          <select value={paidById} onChange={(e) => setPaidById(e.target.value)} className="input">
            <option value="">—</option>
            {concessions.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">{t('po.chargeback.paymentMethod')}</label>
          <select value={method} onChange={(e) => setMethod(e.target.value)} className="input">
            {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{t(`po.chargeback.method.${m}`)}</option>)}
          </select>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="label">{t('po.chargeback.splits')}</label>
          <button type="button" onClick={addSplit} className="text-xs text-cyan-400 hover:text-cyan-300">+ {t('po.chargeback.addSplit')}</button>
        </div>
        {splits.length === 0 && (
          <p className="text-xs text-zinc-500">{t('po.chargeback.noSplits')}</p>
        )}
        <ul className="space-y-2">
          {splits.map((s, i) => (
            <li key={i} className="bg-zinc-900/50 rounded p-2 space-y-2">
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-4">
                  <label className="text-[10px] text-zinc-500 uppercase">{t('po.chargeback.concession')}</label>
                  <select value={s.concessionId} onChange={(e) => updateSplit(i, 'concessionId', e.target.value)} className="input">
                    <option value="">—</option>
                    {concessions.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
                  </select>
                </div>
                <div className="col-span-3">
                  <label className="text-[10px] text-zinc-500 uppercase">{t('po.chargeback.amount')}</label>
                  <input type="number" step="0.01" min={0} value={s.amount} onChange={(e) => updateSplit(i, 'amount', e.target.value)} className="input" placeholder="$" />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] text-zinc-500 uppercase">%</label>
                  <input type="number" step="0.01" min={0} max={100} value={s.percentage} onChange={(e) => updateSplit(i, 'percentage', e.target.value)} className="input" />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] text-zinc-500 uppercase">{t('po.chargeback.rebilledAt')}</label>
                  <input type="date" value={s.rebilledAt} onChange={(e) => updateSplit(i, 'rebilledAt', e.target.value)} className="input" />
                </div>
                <div className="col-span-1 text-right">
                  <button type="button" onClick={() => removeSplit(i)} className="text-zinc-500 hover:text-zinc-300">×</button>
                </div>
              </div>
              <input
                value={s.rebilledNote}
                onChange={(e) => updateSplit(i, 'rebilledNote', e.target.value)}
                placeholder={t('po.chargeback.notePlaceholder')}
                className="input text-xs"
              />
            </li>
          ))}
        </ul>
      </div>
      <div className="flex justify-end">
        <button onClick={save} disabled={busy} className="btn-primary">
          {busy ? t('common.saving') : t('po.chargeback.save')}
        </button>
      </div>
    </div>
  );
}

export default function PODetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [concessions, setConcessions] = useState([]);
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState('FACTURE');

  const reload = () => {
    setLoading(true);
    api.get(`/api/purchase-orders/${id}`)
      .then((res) => setPo(res.data.purchaseOrder))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    api.get('/api/concessions').then((res) => setConcessions(res.data.concessions)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const approve = async () => {
    try {
      await api.post(`/api/purchase-orders/${id}/approve`, {});
      toast.success(t('po.approved'));
      reload();
    } catch (err) { toast.error(err.message); }
  };

  const reject = async () => {
    try {
      await api.post(`/api/purchase-orders/${id}/reject`, {});
      toast.success(t('po.rejected'));
      reload();
    } catch (err) { toast.error(err.message); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    setFiles(Array.from(e.dataTransfer.files || []));
  };

  const uploadNow = async () => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      fd.append('type', uploadType);
      await api.upload(`/api/purchase-orders/${id}/attachments`, fd);
      toast.success(t('po.attachments.uploaded'));
      setFiles([]);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = async (invId) => {
    if (!confirm(t('po.attachments.confirmDelete'))) return;
    try {
      await api.del(`/api/purchase-orders/${id}/attachments/${invId}`);
      toast.success(t('common.deleted'));
      reload();
    } catch (err) { toast.error(err.message); }
  };

  const remove = async () => {
    if (!confirm(t('po.confirmDelete'))) return;
    try {
      await api.del(`/api/purchase-orders/${id}`);
      toast.success(t('common.deleted'));
      navigate('/purchase-orders');
    } catch (err) { toast.error(err.message); }
  };

  if (loading || !po) return <div className="p-6 text-zinc-500 text-sm">{t('common.loading')}</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">PO #{po.number} — <span className="text-zinc-400">{po.itemName}</span></h1>
          <div className="text-xs text-zinc-500 mt-1">{new Date(po.orderedAt).toLocaleDateString('fr-CA')} · {po.vendor}</div>
        </div>
        <div className="flex gap-2">
          <a href={`/api/purchase-orders/${id}/pdf`} target="_blank" rel="noreferrer" className="btn-ghost">{t('po.pdfDownload')}</a>
          <a
            href={`/api/purchase-orders/${id}/pdf?audience=vendor`}
            target="_blank"
            rel="noreferrer"
            className="btn-ghost"
            title={t('po.pdfVendorHint')}
          >
            {t('po.pdfVendor')}
          </a>
          <Link to={`/purchase-orders/${id}/edit`} className="btn-ghost">{t('common.edit')}</Link>
          {user?.role === 'ADMIN' && (
            <button onClick={remove} className="btn-ghost text-rose-400 hover:text-rose-300">{t('common.delete')}</button>
          )}
        </div>
      </div>

      <div className="card p-4 grid grid-cols-2 gap-4 text-sm">
        <Field label={t('po.col.concession')}>
          {po.concessions.map((c) => <span key={c.id} className="inline-block mr-2 text-zinc-300">{c.code}</span>)}
        </Field>
        <Field label={t('po.col.department')}>{po.department?.name || '—'}</Field>
        {po.description && (
          <Field label={t('po.form.description')}><span className="whitespace-pre-wrap">{po.description}</span></Field>
        )}
        <Field label={t('po.form.quantity')}>{po.quantity}</Field>
        <Field label={t('po.form.purchaseType')}>{po.purchaseType || '—'}</Field>
        <Field label={t('po.form.requestedBy')}>{po.requestedBy?.name || '—'}</Field>
        <Field label={t('po.form.requestedFor')}>{po.requestedForName || '—'}</Field>
        <Field label={t('po.form.approver')}>{po.approver?.name || '—'}</Field>
        <Field label={t('po.form.amountBeforeTax')}>{po.amountUnknown ? '???' : formatMoney(po.amountBeforeTax, po.currency)}</Field>
        <Field label={t('po.form.amountAfterTax')}>{po.amountUnknown ? '???' : formatMoney(po.amountAfterTax, po.currency)}</Field>
        <Field label={t('po.form.deliveryDate')}>{po.deliveryTbd ? 'TBD' : (po.deliveryDate ? new Date(po.deliveryDate).toLocaleDateString('fr-CA') : '—')}</Field>
        <Field label={t('po.col.status')}>{t(`po.status.${po.status}`)}</Field>
        <Field label={t('po.col.approval')}>{t(`po.approval.${po.approvalStatus}`)} {po.approvedAt && `· ${new Date(po.approvedAt).toLocaleDateString('fr-CA')}`}</Field>
        <Field label={t('po.form.offBudget')}>{po.isOffBudget ? t('common.yes') : t('common.no')}</Field>
        {po.note && (
          <div className="col-span-2">
            <Field label={t('po.form.note')}><span className="whitespace-pre-wrap">{po.note}</span></Field>
          </div>
        )}
      </div>

      {po.approvalStatus === 'PENDING' && (
        <div className="card p-4 flex items-center justify-between">
          <div className="text-sm text-zinc-400">{t('po.pendingApproval')}</div>
          <div className="flex gap-2">
            <button onClick={reject} className="btn-ghost text-rose-400">{t('po.reject')}</button>
            <button onClick={approve} className="btn-primary">{t('po.approve')}</button>
          </div>
        </div>
      )}

      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold">{t('po.attachments.title')}</h2>
        </div>
        {po.invoices.length === 0 && (
          <p className="text-xs text-zinc-500 mb-3">{t('po.attachments.empty')}</p>
        )}
        {po.invoices.length > 0 && (
          <ul className="space-y-1 mb-3">
            {po.invoices.map((inv) => (
              <li key={inv.id} className="flex items-center justify-between text-xs bg-zinc-900/50 rounded px-2 py-1.5">
                <span className="shrink-0 mr-2 px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 text-[10px] uppercase tracking-wide">
                  {t(`po.invoiceTypes.${inv.type || 'FACTURE'}`)}
                </span>
                <a href={`/api/purchase-orders/${id}/attachments/${inv.id}/download`} className="text-cyan-400 hover:underline truncate flex-1" target="_blank" rel="noreferrer">
                  {inv.filename}
                </a>
                <span className="text-zinc-600 ml-2">{(inv.sizeBytes / 1024).toFixed(1)} KB</span>
                <button onClick={() => removeAttachment(inv.id)} className="ml-3 text-zinc-600 hover:text-rose-400">×</button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-center gap-2 mb-2 text-xs">
          <span className="text-zinc-500">{t('po.invoiceType')}:</span>
          <select value={uploadType} onChange={(e) => setUploadType(e.target.value)} className="input w-auto text-xs py-1">
            <option value="SOUMISSION">{t('po.invoiceTypes.SOUMISSION')}</option>
            <option value="FACTURE">{t('po.invoiceTypes.FACTURE')}</option>
            <option value="FACTURE_SUPP">{t('po.invoiceTypes.FACTURE_SUPP')}</option>
          </select>
        </div>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`rounded-md border-2 border-dashed p-4 text-center text-xs transition-colors ${
            dragOver ? 'border-cyan-500 bg-cyan-900/10' : 'border-zinc-800 bg-zinc-900/30'
          }`}
        >
          <p className="text-zinc-400">{t('po.form.dropFiles')}</p>
          <label className="inline-block mt-2">
            <span className="btn-ghost cursor-pointer text-xs">{t('po.form.browse')}</span>
            <input type="file" multiple className="hidden" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
          </label>
        </div>
        {files.length > 0 && (
          <div className="mt-2 flex items-center justify-between text-xs">
            <span className="text-zinc-400">{files.length} {t('po.attachments.pending')}</span>
            <button onClick={uploadNow} disabled={uploading} className="btn-primary text-xs">
              {uploading ? t('common.saving') : t('po.attachments.upload')}
            </button>
          </div>
        )}
      </div>

      <ChargebackEditor po={po} concessions={concessions} onSaved={reload} />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className="text-sm text-zinc-200 mt-0.5">{children}</div>
    </div>
  );
}
